import { ExternalLink, Paperclip, BookOpen } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { MaterialAudioControls } from "@/components/MaterialAudioControls";

export function RelatedSourcesSection({ evidence, SOURCE_META, onOpenGmail, setPreview, onOpenMaterial }) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <section data-testid="event-related-sources">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
        Relaterade källor ({evidence.length})
      </p>
      <ul className="space-y-2">
        {evidence.map((r, i) => {
          const meta = SOURCE_META[r.source] || { label: r.source, Icon: Paperclip };
          const Icon = meta.Icon;
          const isGmail = r.source === "gmail";
          const isYoutubeMaterial = (
            r.source === "user_upload" &&
            (r.material_kind === "youtube" ||
              (Array.isArray(r.attachments) && r.attachments.some((a) => a.kind === "youtube")))
          );
          return (
            <li
              key={r.message_id || r.url || `${r.source}-${i}-${(r.title || '').slice(0,40)}`}
              className="card-soft p-3 flex gap-3 cursor-pointer hover:bg-[color:var(--bg-elevated)]"
              onClick={async () => {
                if (isGmail && r.message_id) {
                  onOpenGmail?.(r.message_id);
                  return;
                }
                // YouTube-material → öppna inline-spelaren via MaterialPreviewModal
                if (isYoutubeMaterial && r.material_id && onOpenMaterial) {
                  onOpenMaterial(r.material_id);
                  return;
                }
                // Hitta första förhandsvisningsbara bilagan så hela kortet
                // öppnar inline-PDF-viewer (med print/ladda-ner) istället för
                // att skicka iväg användaren till Google Slides/Classroom som
                // ofta saknar print-knapp på mobil. Fall tillbaka till r.url
                // bara om ingen bilaga kan förhandsvisas.
                const firstPreviewable = (r.attachments || []).find((a) => {
                  const m = (a.mime || "").toLowerCase();
                  const t = (a.title || "").toLowerCase();
                  const u = (a.url || "").toLowerCase();
                  const isGDoc = /docs\.google\.com\/(document|spreadsheets|presentation)\//.test(u);
                  return (!!a.proxy_url || !!a.resolve_url) &&
                    (m.includes("pdf") || m.startsWith("image/") ||
                      /\.(pdf|png|jpe?g|gif|webp)(\?|$)/i.test(t) || isGDoc);
                });
                if (firstPreviewable) {
                  let url;
                  if (firstPreviewable.resolve_url) {
                    try {
                      const { data } = await api.get(firstPreviewable.resolve_url);
                      url = data?.url || data?.download_url || data?.signed_url;
                    } catch { /* fallthrough */ }
                  } else if (firstPreviewable.proxy_url) {
                    const token = localStorage.getItem("grindstugatan_token") || "";
                    const base = `${API_BASE}/api${firstPreviewable.proxy_url.replace(/^\/api/, "")}`;
                    url = token ? `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : base;
                  }
                  if (url) {
                    const u = (firstPreviewable.url || "").toLowerCase();
                    const isGDoc = /docs\.google\.com\/(document|spreadsheets|presentation)\//.test(u);
                    setPreview({
                      url,
                      title: firstPreviewable.title || r.title || "Bilaga",
                      mime: isGDoc ? "application/pdf" : firstPreviewable.mime,
                    });
                    return;
                  }
                }
                if (r.url) {
                  window.open(r.url, "_blank", "noopener,noreferrer");
                }
              }}
              data-testid={`event-source-${i}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isGmail ? "bg-[#FDF2EE] text-[#E07A5F]" : "bg-[#EEF2F7] text-[color:var(--jarvis)]"}`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)]">
                  {meta.label}
                  {r.subtitle && <span className="ml-1 text-[color:var(--text-secondary)]">· {r.subtitle}</span>}
                </p>
                <p className="text-sm font-medium leading-snug truncate">{r.title}</p>
                {r.snippet && (
                  <p className="text-xs text-[color:var(--text-tertiary)] mt-1 line-clamp-2">{r.snippet}</p>
                )}
                {/* Audio-uppläsning för uppladdade material (PDF/DOCX/bild/text-analys),
                    EJ för YouTube (har egen video-spelare). */}
                {r.source === "user_upload" &&
                  r.material_id &&
                  r.material_kind !== "youtube" && (
                  <div className="mt-2">
                    <MaterialAudioControls
                      materialId={r.material_id}
                      filename={r.title}
                      compact
                    />
                  </div>
                )}
                {Array.isArray(r.attachments) && r.attachments.length > 0 && (() => {
                  const isImgAtt = (a) =>
                    (a.mime || "").toLowerCase().startsWith("image/") ||
                    /\.(png|jpe?g|gif|webp)(\?|$)/i.test((a.title || "").toLowerCase());
                  const tokenize = (p) => {
                    const token = localStorage.getItem("grindstugatan_token") || "";
                    const base = `${API_BASE}/api${p.replace(/^\/api/, "")}`;
                    return token ? `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : base;
                  };
                  const imagePages = r.attachments
                    .filter((a) => isImgAtt(a) && a.proxy_url)
                    .map((a) => ({ url: tokenize(a.proxy_url), title: a.title || "Bild", mime: a.mime || "image/png" }));
                  const bookUrl =
                    r.stream_item_id && imagePages.length >= 2
                      ? tokenize(`/varmdo/stream-item/${r.stream_item_id}/book.pdf`)
                      : null;
                  return (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`event-source-${i}-attachments`}>
                    {bookUrl && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview({
                            url: bookUrl,
                            title: `${r.title || "Material"} — alla sidor`,
                            mime: "application/pdf",
                          });
                        }}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[color:var(--jarvis)] text-white hover:opacity-90 transition-opacity max-w-full font-medium"
                        data-testid={`event-source-${i}-book-btn`}
                      >
                        <BookOpen size={10} className="flex-shrink-0" />
                        <span>Alla {imagePages.length} sidor (PDF)</span>
                      </button>
                    )}
                    {r.attachments.map((a, ai) => {
                      const mime = (a.mime || "").toLowerCase();
                      const title = a.title || "";
                      const rawUrl = (a.url || "").toLowerCase();
                      // Vklass "Planering" ligger bakom login → visa den redan
                      // skrapade texten inline istället för en död login-länk.
                      if (a.kind === "vklass_plan" && a.plan_text) {
                        const cleanTitle = title.replace(/keyboard_arrow_right/g, "").replace(/assignment_late/g, "").trim() || "Planering";
                        return (
                          <details
                            key={`plan-${ai}`}
                            className="w-full bg-[#EEF2F7] rounded-md px-2.5 py-2"
                            data-testid={`event-source-${i}-plan-${ai}`}
                          >
                            <summary className="text-[11px] font-medium text-[color:var(--jarvis)] cursor-pointer flex items-center gap-1 list-none">
                              <Paperclip size={10} className="flex-shrink-0" />
                              <span className="truncate">{cleanTitle}</span>
                            </summary>
                            <pre className="text-[11px] text-[color:var(--text-secondary)] whitespace-pre-wrap mt-2 font-sans leading-relaxed">
                              {a.plan_text}
                            </pre>
                          </details>
                        );
                      }
                      // Google Docs/Sheets/Slides streamas av backend som PDF
                      // → visa i vår PdfViewerModal så användaren får print/ladda-ner-knappar
                      // (browser-native PDF-viewer saknar dessa i många mobiler/iOS).
                      const isGoogleDoc = /docs\.google\.com\/(document|spreadsheets|presentation)\//.test(rawUrl);
                      const canPreview =
                        (!!a.proxy_url || !!a.resolve_url) &&
                        (mime.includes("pdf") ||
                          mime.startsWith("image/") ||
                          /\.(pdf|png|jpe?g|gif|webp)(\?|$)/i.test(title) ||
                          isGoogleDoc);
                      // För Google-dokument vet vi att proxy-svaret är PDF —
                      // skicka rätt mime så PdfViewerModal ritar med react-pdf.
                      const previewMime = isGoogleDoc ? "application/pdf" : a.mime;
                      return (
                        <button
                          key={a.proxy_url || a.resolve_url || a.url || `att-${ai}-${(a.title || '').slice(0,40)}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            // YouTube-bilaga → öppna inline-spelaren via MaterialPreviewModal
                            if (a.kind === "youtube" && r.material_id && onOpenMaterial) {
                              onOpenMaterial(r.material_id);
                              return;
                            }
                            let url;
                            if (a.resolve_url) {
                              // Hämta presigned download-URL via backend
                              try {
                                const { data } = await api.get(a.resolve_url);
                                url = data?.url || data?.download_url || data?.signed_url;
                              } catch {
                                return;
                              }
                            } else if (a.proxy_url) {
                              const token = localStorage.getItem("grindstugatan_token") || "";
                              const base = `${API_BASE}/api${a.proxy_url.replace(/^\/api/, "")}`;
                              url = token ? `${base}${base.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : base;
                            } else {
                              url = a.url;
                            }
                            if (!url) return;
                            if (canPreview) {
                              const isImg = isImgAtt(a);
                              const startIndex = isImg
                                ? Math.max(0, imagePages.findIndex((p) => p.title === (a.title || "Bild")))
                                : 0;
                              setPreview({
                                url,
                                title: title || "Bilaga",
                                mime: previewMime,
                                pages: isImg && imagePages.length > 1 ? imagePages : undefined,
                                startIndex,
                              });
                            } else {
                              window.open(url, "_blank", "noopener,noreferrer");
                            }
                          }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[#EEF2F7] hover:bg-[#DEE5EE] text-[color:var(--jarvis)] transition-colors max-w-full"
                          data-testid={`event-source-${i}-att-${ai}`}
                        >
                          <Paperclip size={10} className="flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{a.title || "Bilaga"}</span>
                        </button>
                      );
                    })}
                  </div>
                  );
                })()}
                {r.has_attachments && !(Array.isArray(r.attachments) && r.attachments.length > 0) && (
                  <p className="text-[11px] text-[color:var(--text-tertiary)] mt-1.5 flex items-center gap-1">
                    <Paperclip size={9} /> Bilagor finns (synka för att visa)
                  </p>
                )}
              </div>
              {(r.url || isGmail) && (
                <ExternalLink size={12} className="text-[color:var(--text-tertiary)] flex-shrink-0 mt-2" />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
